import { useState } from "react";
import { FileDown, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const PDF_URL = "/resources/tworay-m365-security-baseline-checklist.pdf";
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const TOPICS = [
  "Enforce MFA everywhere",
  "Dedicated admin accounts",
  "Disable legacy auth",
  "Independent M365 backup",
  "Anti-phishing & Safe Links",
  "Audit logging & access reviews",
];

export function LeadMagnet({ variant = "section" }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!EMAIL_RE.test(email.trim())) return setErr("Please enter a valid email address.");
    if (!ack) return setErr("Please accept the Privacy Policy to continue.");
    setBusy(true);
    try {
      const currentUrl =
        typeof window !== "undefined" ? new URL(window.location.href) : null;

      await api.post("/leads", {
        name: firstName.trim() || email.trim(),
        email: email.trim(),
        service_interest: "Microsoft 365",
        message: "Requested the 10-Point Microsoft 365 Security Baseline Checklist.",
        privacy_ack: true,
        marketing_opt_in: true,
        lead_channel: "Lead Magnet",
        enquiry_type: "Resource Download",
        landing_page: currentUrl?.href || "",
        referrer:
          typeof document !== "undefined" ? document.referrer : "",
        utm_source: currentUrl?.searchParams.get("utm_source") || "",
        utm_medium: currentUrl?.searchParams.get("utm_medium") || "",
        utm_campaign: currentUrl?.searchParams.get("utm_campaign") || "",
        utm_content: currentUrl?.searchParams.get("utm_content") || "",
        source: "lead_magnet",
      });
      setDone(true);

    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // ---------- Compact (footer) ----------
  if (variant === "compact") {
    return (
      <div data-testid="lead-magnet-compact">
        <div className="flex items-center gap-2 text-cyan">
          <FileDown size={16} />
          <span className="text-xs font-semibold tracking-[0.2em] uppercase">Free checklist</span>
        </div>
        <p className="mt-3 text-sm text-white/80 leading-relaxed">
          Not ready to book? Get our free 10-Point Microsoft 365 Security Baseline Checklist for UK SMEs.
        </p>
        {done ? (
          <a href={PDF_URL} download="TWORAY-Microsoft-365-Security-Baseline-Checklist.pdf" data-testid="lead-magnet-download" className="mt-4 inline-flex items-center gap-2 bg-cyan text-navy font-semibold text-sm px-4 py-2.5 rounded-sm hover:bg-white transition-colors">
            <FileDown size={15} /> Download the checklist
          </a>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-2" data-testid="lead-magnet-form-compact">
            <input type="email" placeholder="Work email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="lead-magnet-email" className="w-full rounded-sm bg-white/10 border border-white/20 px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-cyan" />
            <label className="flex items-start gap-2 text-xs text-white/60">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="lead-magnet-ack" className="mt-0.5 h-3.5 w-3.5 accent-[#19AFFF]" />
              <span>I agree to the <a href="/legal/privacy" className="text-cyan hover:underline">Privacy Policy</a> and to occasional advisory emails.</span>
            </label>
            {err && <p className="text-xs text-red-300" data-testid="lead-magnet-error">{err}</p>}
            <button type="submit" disabled={busy} data-testid="lead-magnet-submit" className="w-full inline-flex items-center justify-center gap-2 bg-cyan text-navy font-semibold text-sm px-4 py-2.5 rounded-sm hover:bg-white transition-colors disabled:opacity-50">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} {busy ? "Sending…" : "Send me the checklist"}
            </button>
          </form>
        )}
      </div>
    );
  }

  // ---------- Section (page) ----------
  return (
    <div data-testid="lead-magnet-section" className="rounded-md border border-brandborder bg-white overflow-hidden grid md:grid-cols-2">
      <div className="bg-navy text-white p-8 sm:p-10 grain-overlay relative">
        <div className="relative z-10">
          <div className="flex items-center gap-2 text-cyan">
            <ShieldCheck size={18} />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase">Free checklist</span>
          </div>
          <h3 className="mt-4 font-heading font-bold text-2xl sm:text-3xl leading-tight">
            The 10-Point Microsoft 365 Security Baseline
          </h3>
          <p className="mt-3 text-white/70 text-sm leading-relaxed">
            A practical, vendor-agnostic checklist for UK SMEs. Not ready to book a call? Start here and sense-check your posture.
          </p>
          <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2">
            {TOPICS.map((t) => (
              <li key={t} className="flex items-center gap-2 text-sm text-white/85">
                <CheckCircle2 size={15} className="text-cyan shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="p-8 sm:p-10 flex flex-col justify-center">
        {done ? (
          <div className="text-center" data-testid="lead-magnet-done">
            <CheckCircle2 size={40} className="mx-auto text-brandblue" />
            <h4 className="mt-4 font-heading font-bold text-xl text-navy">Your checklist is ready</h4>
            <p className="mt-2 text-sm text-slate">Your checklist is ready. Use the button below to download it.</p>
            <a href={PDF_URL} download="TWORAY-Microsoft-365-Security-Baseline-Checklist.pdf" data-testid="lead-magnet-download" className="mt-6 inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold text-sm px-6 py-3 rounded-sm hover:bg-brandblue transition-colors">
              <FileDown size={16} /> Download the PDF
            </a>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="lead-magnet-form">
            <div>
              <label className="text-xs text-slate">First name (optional)</label>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="lead-magnet-name" className="mt-1 w-full rounded-sm border border-brandborder bg-white px-3 py-2.5 text-sm text-navy outline-none focus:border-brandblue" />
            </div>
            <div>
              <label className="text-xs text-slate">Work email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="lead-magnet-email" className="mt-1 w-full rounded-sm border border-brandborder bg-white px-3 py-2.5 text-sm text-navy outline-none focus:border-brandblue" />
            </div>
            <label className="flex items-start gap-2 text-xs text-slate">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="lead-magnet-ack" className="mt-0.5 h-4 w-4 accent-[#0968F0]" />
              <span>I agree to TWORAY sending me the checklist and occasional advisory emails, per the <a href="/legal/privacy" className="text-brandblue font-medium hover:underline">Privacy Policy</a>.</span>
            </label>
            {err && <p className="text-xs text-destructive" data-testid="lead-magnet-error">{err}</p>}
            <button type="submit" disabled={busy} data-testid="lead-magnet-submit" className="w-full inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold text-sm px-6 py-3 rounded-sm hover:bg-brandblue transition-colors disabled:opacity-50">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />} {busy ? "Sending…" : "Send me the checklist"}
            </button>
            <p className="text-[11px] text-slate text-center">No spam. Unsubscribe anytime. Guidance only — not a scoped assessment.</p>
          </form>
        )}
      </div>
    </div>
  );
}
